// =====================================================
// CONFIGURAÇÃO DE PLANOS - CENTRALIZADO
// Arquivo: FillSign.Website/src/lib/plans.ts
// Use este mesmo arquivo em Website e App
// =====================================================

export interface PlanConfig {
  id: string;
  name: string;
  signatures: number; // -1 = unlimited
  users: number; // -1 = unlimited
  price: { brl: number; usd: number };
  features: string[];
  requiresPayment: boolean;
  contactSales?: boolean;
}

// Ler configurações de ambiente se disponíveis
function getEnvNumber(key: string, defaultValue: number): number {
  const value = import.meta.env[key];
  if (value !== undefined && value !== '') {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
}

// Planos configuráveis via variáveis de ambiente
export const PLANS: Record<string, PlanConfig> = {
  free: {
    id: "free",
    name: "Free",
    signatures: getEnvNumber('VITE_PLAN_FREE_SIGNATURES', 3),
    users: getEnvNumber('VITE_PLAN_FREE_USERS', 1),
    price: { brl: 0, usd: 0 },
    features: ["basic_templates", "email_support"],
    requiresPayment: false,
  },
  pro: {
    id: "pro",
    name: "Professional",
    signatures: getEnvNumber('VITE_PLAN_PRO_SIGNATURES', 120),
    users: getEnvNumber('VITE_PLAN_PRO_USERS', 5),
    price: { 
      brl: getEnvNumber('VITE_PLAN_PRO_PRICE_BRL', 199), 
      usd: getEnvNumber('VITE_PLAN_PRO_PRICE_USD', 39) 
    },
    features: ["custom_templates", "priority_support", "api", "webhooks"],
    requiresPayment: true,
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    signatures: getEnvNumber('VITE_PLAN_ENTERPRISE_SIGNATURES', 300),
    users: getEnvNumber('VITE_PLAN_ENTERPRISE_USERS', 8),
    price: { 
      brl: getEnvNumber('VITE_PLAN_ENTERPRISE_PRICE_BRL', 599), 
      usd: getEnvNumber('VITE_PLAN_ENTERPRISE_PRICE_USD', 119) 
    },
    features: ["all_pro", "dynamics365", "sso_saml", "support_24_7", "advanced_api"],
    requiresPayment: true,
  },
  custom: {
    id: "custom",
    name: "Custom",
    signatures: -1,
    users: -1,
    price: { brl: 0, usd: 0 },
    features: ["all_enterprise", "dedicated_support", "consulting", "training", "on_premise"],
    requiresPayment: false,
    contactSales: true,
  },
};

// IDs DOS PREÇOS NO STRIPE
export const STRIPE_PRICE_IDS = {
  pro: {
    monthly: import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID || "price_pro_monthly",
    yearly: import.meta.env.VITE_STRIPE_PRO_YEARLY_PRICE_ID || "price_pro_yearly",
  },
  enterprise: {
    monthly: import.meta.env.VITE_STRIPE_ENTERPRISE_MONTHLY_PRICE_ID || "price_enterprise_monthly",
    yearly: import.meta.env.VITE_STRIPE_ENTERPRISE_YEARLY_PRICE_ID || "price_enterprise_yearly",
  },
};

// FUNÇÕES UTILITÁRIAS
export function planRequiresPayment(planId: string): boolean {
  const plan = PLANS[planId as keyof typeof PLANS];
  return plan?.requiresPayment ?? false;
}

export function getStripeCheckoutUrl(planId: string, userId: string, successUrl: string, cancelUrl: string): string {
  return `/api/v1/subscriptions/create-checkout-session?plan=${planId}&userId=${userId}&successUrl=${encodeURIComponent(successUrl)}&cancelUrl=${encodeURIComponent(cancelUrl)}`;
}

// Plan texts come from the admin panel in pt/en. Spanish uses the API's `es` text when there is one, else the
// default descriptions below, else the English text.
const PLAN_TEXT_ES: Record<string, string> = {
  "To start experimenting": "Para empezar a probar",
  "For businesses": "Para empresas",
  "For enterprises": "Para grandes empresas",
  "Custom solutions": "Soluciones a medida",
};

export function planTextEs(en: string | undefined, es?: string | null): string {
  return (es && es.trim()) || PLAN_TEXT_ES[(en || "").trim()] || en || "";
}

export function formatPlanLimit(value: number, language: "pt" | "en" | "es"): string {
  if (value === -1) {
    return language === "pt" ? "Ilimitado" : language === "es" ? "Ilimitado" : "Unlimited";
  }
  return value.toString();
}

// DADOS PARA EXIBIÇÃO
export interface PlanDisplayInfo {
  id: string;
  name: string;
  price: { pt: string; en: string };
  period: { pt: string; en: string };
  description: { pt: string; en: string };
  signatures: { pt: string; en: string };
  users: { pt: string; en: string };
  features: Array<{ pt: string; en: string; included: boolean }>;
  popular?: boolean;
  contactSales?: boolean;
}

export function getPlanDisplayInfo(): PlanDisplayInfo[] {
  return [
    {
      id: "free",
      name: "Free",
      price: { pt: "R$ 0", en: "$0" },
      period: { pt: "/mês", en: "/month" },
      description: { pt: "Para começar a experimentar", en: "To start experimenting" },
      signatures: { pt: `${PLANS.free.signatures} documentos/mês`, en: `${PLANS.free.signatures} documents/month` },
      users: { pt: `${PLANS.free.users} usuário`, en: `${PLANS.free.users} user` },
      features: [
        { pt: `${PLANS.free.signatures} documentos/mês`, en: `${PLANS.free.signatures} documents/month`, included: true },
        { pt: `${PLANS.free.users} usuário`, en: `${PLANS.free.users} user`, included: true },
        { pt: "Templates básicos", en: "Basic templates", included: true },
        { pt: "Suporte por email", en: "Email support", included: true },
        { pt: "API de integração", en: "Integration API", included: false },
        { pt: "Integração Dynamics 365", en: "Dynamics 365 integration", included: false },
      ],
    },
    {
      id: "pro",
      name: "Professional",
      price: { pt: `R$ ${PLANS.pro.price.brl}`, en: `$${PLANS.pro.price.usd}` },
      period: { pt: "/mês", en: "/month" },
      description: { pt: "Para pequenas e médias empresas", en: "For small and medium businesses" },
      signatures: { pt: `${PLANS.pro.signatures} documentos/mês`, en: `${PLANS.pro.signatures} documents/month` },
      users: { pt: `Até ${PLANS.pro.users} usuários`, en: `Up to ${PLANS.pro.users} users` },
      features: [
        { pt: `${PLANS.pro.signatures} documentos/mês`, en: `${PLANS.pro.signatures} documents/month`, included: true },
        { pt: `Até ${PLANS.pro.users} usuários`, en: `Up to ${PLANS.pro.users} users`, included: true },
        { pt: "Templates personalizados", en: "Custom templates", included: true },
        { pt: "Suporte prioritário", en: "Priority support", included: true },
        { pt: "API de integração", en: "Integration API", included: true },
        { pt: "Integração Dynamics 365", en: "Dynamics 365 integration", included: false },
      ],
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: { pt: `R$ ${PLANS.enterprise.price.brl}`, en: `$${PLANS.enterprise.price.usd}` },
      period: { pt: "/mês", en: "/month" },
      description: { pt: "Para empresas em crescimento", en: "For growing enterprises" },
      signatures: { pt: `${PLANS.enterprise.signatures} documentos/mês`, en: `${PLANS.enterprise.signatures} documents/month` },
      users: { pt: `Até ${PLANS.enterprise.users} usuários`, en: `Up to ${PLANS.enterprise.users} users` },
      features: [
        { pt: `${PLANS.enterprise.signatures} documentos/mês`, en: `${PLANS.enterprise.signatures} documents/month`, included: true },
        { pt: `Até ${PLANS.enterprise.users} usuários`, en: `Up to ${PLANS.enterprise.users} users`, included: true },
        { pt: "Suporte 24/7", en: "24/7 support", included: true },
        { pt: "API avançada", en: "Advanced API", included: true },
        { pt: "Integração Dynamics 365", en: "Dynamics 365 integration", included: true },
        { pt: "SSO / SAML", en: "SSO / SAML", included: true },
      ],
      popular: true,
    },
    {
      id: "custom",
      name: "Custom",
      price: { pt: "Sob consulta", en: "Contact us" },
      period: { pt: "", en: "" },
      description: { pt: "Soluções personalizadas", en: "Custom solutions" },
      signatures: { pt: "Ilimitado", en: "Unlimited" },
      users: { pt: "Ilimitado", en: "Unlimited" },
      features: [
        { pt: "Tudo do Enterprise", en: "Everything in Enterprise", included: true },
        { pt: "Documentos ilimitados", en: "Unlimited documents", included: true },
        { pt: "Usuários ilimitados", en: "Unlimited users", included: true },
        { pt: "Gerente de conta dedicado", en: "Dedicated account manager", included: true },
        { pt: "SLA personalizado", en: "Custom SLA", included: true },
        { pt: "Implantação on-premise", en: "On-premise deployment", included: true },
      ],
      contactSales: true,
    },
  ];
}
